/**
 * browser-uuid.ts — a v4 UUID that works outside a secure context (#12799).
 *
 * WHY THIS EXISTS
 * ---------------
 * `crypto.randomUUID()` is defined ONLY in a secure context: HTTPS, or the
 * `http://localhost` / `http://127.0.0.1` special cases. Every other origin —
 * including a worktree dev server reached over Tailscale or the LAN
 * (`http://cadence-one:3799`, `http://100.90.96.19:3799`) — gets `undefined`,
 * and a direct call throws `crypto.randomUUID is not a function` from inside
 * `useState`/`useMemo`, i.e. during render, i.e. as an error boundary instead
 * of a page. Measured against the running dev server at #12799:
 *
 *   origin                     isSecureContext  randomUUID  getRandomValues  subtle
 *   http://localhost:3792      true             function    function         object
 *   http://100.90.96.19:3792   false            undefined   function         undefined
 *
 * `getRandomValues` is NOT gated on a secure context, so the fallback below is
 * still cryptographically strong. That is the whole reason this module exists
 * rather than a `Math.random()` shim: 11 client call sites needed a
 * non-secure-context path, and none of them needed to become weaker to get it.
 *
 * WHY IT THROWS INSTEAD OF DEGRADING FURTHER
 * ------------------------------------------
 * Every caller mints an identifier used for correlation (analytics session
 * ids), idempotency (submission ids) or record identity (temp attachable ids).
 * A `Math.random()`-derived id satisfies "looks like a UUID" and is silently
 * wrong forever; a throw is noticed on the first run. `src/lib/submission-cache.ts`
 * carried a `Math.random()` fallback of exactly that shape and now calls this
 * instead: its id is the server-side idempotency key, and `reflections.ts`
 * documents an unguessability property that the weak branch did not hold.
 *
 * CLIENT-SAFE LEAF. No `server-only`, no Prisma, no other imports at all: this
 * is imported by `'use client'` components and hooks, so it must add nothing to
 * their bundle beyond itself.
 *
 * Enforced by `pnpm run check:no-direct-crypto-randomuuid`, which rejects a
 * bare `crypto.randomUUID` under `src/components/**` and `src/hooks/**`.
 */

/**
 * The subset of `Crypto` this module can use.
 *
 * Both members are OPTIONAL on purpose — that optionality is the bug being
 * modelled. In an insecure context the real `crypto` object satisfies this
 * type with `randomUUID` absent, and TypeScript's `Crypto` (which types
 * `randomUUID` as always present) is what let 11 call sites typecheck cleanly
 * while crashing at runtime.
 */
export interface RandomSource {
  randomUUID?: () => string
  getRandomValues?: <T extends ArrayBufferView | null>(array: T) => T
}

/** Bytes in a UUID. RFC 9562 §4. */
const UUID_BYTE_LENGTH = 16

/**
 * Written into the entropy buffer before it is handed to `getRandomValues`, so
 * that "the source wrote zeros" (legal) can be told apart from "the source
 * wrote nothing" (broken). Any non-zero value works; see the check that reads
 * it for why an all-zero test cannot do this job.
 */
const POISON_BYTE = 0xa5

/**
 * Returns a random RFC 9562 v4 UUID, working in a non-secure context.
 *
 * Resolution order:
 *   1. `source.randomUUID()` — the native implementation, whenever it exists.
 *   2. `source.getRandomValues()` — 16 bytes, with the version and variant
 *      fields set per RFC 9562 §5.4.
 *   3. throw — rather than return a weak id (see the module header).
 *
 * CALL IT LAZILY IN A COMPONENT. Every id here costs a CSPRNG draw, so the
 * argument position matters: `useState(() => safeRandomUUID())` and
 * `useMemo(() => safeRandomUUID(), [])` evaluate once, while
 * `useRef(safeRandomUUID())` evaluates on EVERY render and `useRef` discards
 * all but the first — a draw per render for a value thrown away. Where a ref
 * is the right container because the id is reassigned later (see
 * `components/edit-item-modal.tsx`'s `tempRockId`), seed it empty and fill it
 * on first render: `const r = useRef(''); if (!r.current) r.current =
 * safeRandomUUID()`. Flagged in the #12811 review of this change, which had
 * left one eager call site among ten lazy ones.
 *
 * @param source Where entropy comes from. Defaults to the ambient
 *   `globalThis.crypto`, or `null` when the environment exposes no WebCrypto
 *   at all. Passing an explicit source is how tests exercise the
 *   insecure-context path without mutating a global — `tests/unit/lib/**` runs
 *   with `isolate: false`, where a patched `globalThis.crypto` leaks into every
 *   later file in the worker. Production callers pass nothing.
 */
export function safeRandomUUID(
  source: RandomSource | null = typeof globalThis.crypto === 'undefined'
    ? null
    : globalThis.crypto,
): string {
  if (source?.randomUUID) return source.randomUUID()

  if (source?.getRandomValues) {
    // The returned array WHEN THERE IS ONE, else the buffer we allocated. Per
    // spec `getRandomValues` fills in place and returns the same array, so the
    // two are the same object — but the fallback covers a shim that fills
    // correctly and returns `undefined`, which is a real polyfill shape and
    // which reading the return value alone turned into a throw claiming no
    // entropy was available when it was (#12811 review).
    //
    // Reading the local UNCONDITIONALLY would be wrong in the other direction,
    // and the paired test says so: a shim that returns a 4-byte buffer without
    // filling ours would leave sixteen zero bytes, and this function would
    // fabricate a UUID out of them — exactly what the length check below exists
    // to refuse. `??` keeps both failure modes covered.
    const buffer = new Uint8Array(UUID_BYTE_LENGTH).fill(POISON_BYTE)
    const bytes = source.getRandomValues(buffer) ?? buffer
    if (!bytes || bytes.length !== UUID_BYTE_LENGTH) {
      throw new Error(
        'safeRandomUUID: crypto.getRandomValues did not return 16 bytes, so a ' +
          'v4 UUID cannot be derived from it. Padding the buffer would ' +
          'fabricate entropy, so this fails instead.',
      )
    }

    // A source that handed our buffer straight back WITHOUT WRITING TO IT is
    // the gap `?? buffer` opened, and it is worse than the bug that fallback
    // fixed because it is silent: sixteen untouched bytes pass the length check
    // and the masking below stamps them into a well-formed v4 that never
    // changes. Reproduced with `getRandomValues: b => b` (#12811 review), which
    // returned `00000000-0000-4000-8000-000000000000` — and `generateSubmissionId`
    // mints a capability from this, so one constant id is a cross-user
    // collision rather than a cosmetic defect.
    //
    // THE PROBE IS A POISON PATTERN, NOT AN ALL-ZERO CHECK, and the difference
    // is the whole point: all-zero entropy is LEGAL — a real CSPRNG produces it
    // with probability 2^-128, and the paired test asserts this function still
    // writes the version and variant nibbles over it. An all-zero test cannot
    // tell that legal draw apart from a source that wrote nothing, so it would
    // reject a documented input to catch a broken one. Poisoning can: a source
    // that writes zeros clears the poison, a source that writes nothing leaves
    // it. The residual false positive is a source drawing exactly sixteen
    // POISON_BYTEs, which is the same 2^-128 as the case above without
    // rejecting anything the tests call legal.
    //
    // Guarded on `bytes === buffer`: when the source returns a DIFFERENT array
    // we never poisoned it, so the pattern would prove nothing about it.
    if (bytes === buffer) {
      let wrote = false
      for (let i = 0; i < UUID_BYTE_LENGTH; i += 1) {
        if (buffer[i] !== POISON_BYTE) {
          wrote = true
          break
        }
      }
      if (!wrote) {
        throw new Error(
          'safeRandomUUID: crypto.getRandomValues returned the buffer it was ' +
            'given without writing to it, so there is no entropy in it. ' +
            'Deriving a UUID from an unwritten buffer would mint the same id ' +
            'for every caller, so this fails instead.',
        )
      }
    } else {
      // A DIFFERENT array, which we never poisoned — so the check above cannot
      // speak for it, and skipping it outright left the same constant
      // `00000000-0000-4000-8000-000000000000` reachable through the other
      // return shape. That is the round-4 hole surviving its own fix, found in
      // the round-5 review; `getRandomValues: () => new Uint8Array(16)`
      // reproduces it.
      //
      // All-zero is the only signal available here, and on THIS branch it is
      // sound where it would not be on the branch above. A spec-compliant
      // source fills the array it was given and returns that same array, so a
      // legal all-zero draw arrives as `bytes === buffer` and is handled by the
      // poison probe. Reaching here at all means the source substituted its own
      // array; one that also happens to hold sixteen zeros is either unfilled or
      // a 2^-128 coincidence, and refusing both beats minting a shared
      // capability id (see the module header).
      let nonZero = false
      for (let i = 0; i < UUID_BYTE_LENGTH; i += 1) {
        if (bytes[i] !== 0) {
          nonZero = true
          break
        }
      }
      if (!nonZero) {
        throw new Error(
          'safeRandomUUID: crypto.getRandomValues returned a different, ' +
            'all-zero buffer, so it holds no entropy. Deriving a UUID from it ' +
            'would mint the same id for every caller, so this fails instead.',
        )
      }
    }

    // RFC 9562 §5.4. Both operations MASK before they set: an OR alone leaves
    // the caller's high bits in place, which produces a UUID that claims to be
    // v4 only when the random byte happened to be small enough.
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 0b10 (IETF)

    let hex = ''
    for (let i = 0; i < UUID_BYTE_LENGTH; i += 1) {
      hex += bytes[i].toString(16).padStart(2, '0')
    }

    return (
      `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
      `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
    )
  }

  throw new Error(
    'safeRandomUUID: this environment exposes neither crypto.randomUUID nor ' +
      'crypto.getRandomValues, so no cryptographically strong id can be ' +
      'produced. Returning a Math.random()-derived id instead would be ' +
      'silently weak, so this fails loudly. If you are seeing this in a ' +
      'browser, the page has no WebCrypto at all — check the origin.',
  )
}
